// Profile tab editing (B6) — financial history with "Add period" + per-row
// edit (Loans.Update). New borrower financials arrive annually (FR-Q4 /
// FR-A5 reporting needs fresh trailing-12 data); rows added after close
// carry the "Added post-close" provenance vs the carried rows' Deal link.

import { useState } from "react";
import { Button, Card } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { formatCurrency } from "@/components/deals/dealFormat";
import {
  useAddLoanFinancialPeriod,
  useUpdateLoanFinancialPeriod,
} from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import type { FinancialHistoryRow } from "@/types/intakeProfile";

interface FinancialHistoryCardProps {
  loanId: string;
  rows: FinancialHistoryRow[];
  caption: React.ReactNode;
  canEdit: boolean;
}

interface PeriodDraft {
  year: string;
  periodType: "Annual" | "TTM";
  revenue: string;
  ebitda: string;
}

function hydrate(row?: FinancialHistoryRow): PeriodDraft {
  return {
    year: row ? String(row.year) : "",
    periodType: row?.periodType ?? "Annual",
    revenue: row?.revenue != null ? String(row.revenue) : "",
    ebitda: row?.ebitda != null ? String(row.ebitda) : "",
  };
}

function parseMoney(label: string, raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed.replace(/[$,]/g, ""));
  if (!Number.isFinite(n)) throw new Error(`${label} must be a number.`);
  return n;
}

export function FinancialHistoryCard({
  loanId,
  rows,
  caption,
  canEdit,
}: FinancialHistoryCardProps) {
  const toast = useToast();
  const add = useAddLoanFinancialPeriod(loanId);
  const update = useUpdateLoanFinancialPeriod(loanId);

  // null = closed; "new" = add; otherwise the row being edited.
  const [editing, setEditing] = useState<"new" | FinancialHistoryRow | null>(null);
  const [draft, setDraft] = useState<PeriodDraft>(hydrate());
  const [error, setError] = useState<string | null>(null);

  const openModal = (target: "new" | FinancialHistoryRow) => {
    setEditing(target);
    setDraft(hydrate(target === "new" ? undefined : target));
    setError(null);
  };

  const save = async () => {
    setError(null);
    const year = Number(draft.year);
    if (!Number.isInteger(year) || year < 1900 || year > 2100) {
      setError("Year must be between 1900 and 2100.");
      return;
    }
    let revenue: number | null;
    let ebitda: number | null;
    try {
      revenue = parseMoney("Revenue", draft.revenue);
      ebitda = parseMoney("NOI", draft.ebitda);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      return;
    }
    const body = { year, periodType: draft.periodType, revenue, ebitda };
    try {
      if (editing === "new") {
        await add.mutateAsync(body);
        toast.success("Period added");
      } else if (editing) {
        // FIND-12: open-edit-save-unchanged is a zero-change no-op.
        const unchanged =
          editing.year === year &&
          editing.periodType === draft.periodType &&
          (editing.revenue ?? null) === revenue &&
          (editing.ebitda ?? null) === ebitda;
        if (!unchanged) {
          await update.mutateAsync({ rowId: editing.id!, body });
          toast.success("Period updated");
        }
      }
      setEditing(null);
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const inputClass =
    "mt-1 w-full border border-border-light rounded-md px-space-2 py-space-1 text-small bg-white-pure";

  return (
    <Card data-testid="financial-history-card">
      <div className="mb-space-3 flex items-start justify-between gap-space-3">
        <div>
          <div className="font-display font-bold text-h3 text-onyx">
            Financial history
          </div>
          <p className="text-caption text-gray-mid m-0">{caption}</p>
        </div>
        {canEdit && (
          <Button
            variant="tertiary"
            size="sm"
            onClick={() => openModal("new")}
            data-testid="financial-history-add"
          >
            + Add period
          </Button>
        )}
      </div>

      {rows.length === 0 ? (
        <p className="text-small text-gray-mid m-0">
          No financial history yet. Add the first period.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-small border-collapse">
            <thead>
              <tr className="text-left border-b border-border-light">
                <Th>Year</Th>
                <Th>Period</Th>
                <Th className="text-right">Revenue</Th>
                <Th className="text-right">NOI</Th>
                <Th>Provenance</Th>
                {canEdit && <Th className="text-right">{""}</Th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id ?? `${r.year}-${r.periodType}`}
                  className="border-b border-border-light"
                  data-testid={`financial-history-row-${r.year}-${r.periodType}`}
                >
                  <Td>{r.year}</Td>
                  <Td>{r.periodType === "TTM" ? "TTM" : "Annual"}</Td>
                  <Td className="text-right font-mono tabular-nums">
                    {r.revenue != null ? formatCurrency(r.revenue) : "—"}
                  </Td>
                  <Td className="text-right font-mono tabular-nums">
                    {r.ebitda != null ? formatCurrency(r.ebitda) : "—"}
                  </Td>
                  <Td>
                    <span className="text-caption text-gray-mid">
                      {r.addedPostClose ? "Added post-close" : "Carried from Deal"}
                    </span>
                  </Td>
                  {canEdit && (
                    <Td className="text-right">
                      {r.id && (
                        <Button
                          variant="tertiary"
                          size="sm"
                          onClick={() => openModal(r)}
                          data-testid={`financial-history-edit-${r.year}-${r.periodType}`}
                        >
                          Edit
                        </Button>
                      )}
                    </Td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === "new" ? "Add financial period" : "Edit financial period"}
        footer={
          <>
            {error && (
              <p
                className="text-caption text-status-error m-0 mr-auto"
                role="alert"
                data-testid="financial-history-error"
              >
                {error}
              </p>
            )}
            <Button variant="secondary" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => void save()}
              loading={add.isPending || update.isPending}
              data-testid="financial-history-save"
            >
              Save
            </Button>
          </>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3">
          <div>
            <label
              htmlFor="period-year"
              className="font-display text-caption uppercase tracking-label text-gray-mid"
            >
              Year
            </label>
            <input
              id="period-year"
              type="text"
              inputMode="numeric"
              className={inputClass}
              value={draft.year}
              onChange={(e) => setDraft((d) => ({ ...d, year: e.target.value }))}
              data-testid="period-year"
            />
          </div>
          <div>
            <label
              htmlFor="period-type"
              className="font-display text-caption uppercase tracking-label text-gray-mid"
            >
              Period type
            </label>
            <select
              id="period-type"
              className={inputClass}
              value={draft.periodType}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  periodType: e.target.value as "Annual" | "TTM",
                }))
              }
              data-testid="period-type"
            >
              <option value="Annual">Annual</option>
              <option value="TTM">TTM</option>
            </select>
          </div>
          <div>
            <label
              htmlFor="period-revenue"
              className="font-display text-caption uppercase tracking-label text-gray-mid"
            >
              Revenue
            </label>
            <input
              id="period-revenue"
              type="text"
              inputMode="decimal"
              className={inputClass}
              value={draft.revenue}
              onChange={(e) => setDraft((d) => ({ ...d, revenue: e.target.value }))}
              data-testid="period-revenue"
            />
          </div>
          <div>
            <label
              htmlFor="period-ebitda"
              className="font-display text-caption uppercase tracking-label text-gray-mid"
            >
              NOI
            </label>
            <input
              id="period-ebitda"
              type="text"
              inputMode="decimal"
              className={inputClass}
              value={draft.ebitda}
              onChange={(e) => setDraft((d) => ({ ...d, ebitda: e.target.value }))}
              data-testid="period-ebitda"
            />
          </div>
        </div>
      </Modal>
    </Card>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      className={`font-display text-caption uppercase tracking-label text-gray-mid py-space-1 ${className ?? ""}`}
    >
      {children}
    </th>
  );
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={`py-space-1 text-onyx ${className ?? ""}`}>{children}</td>;
}
